<script lang="ts" setup>
import { defineEmits,ref, onMounted, defineProps, computed, watch } from 'vue';
import { Button } from '@/components/ui/button';
import MediaThumb from '@/components/MediaThumb.vue';  
import { Media } from '@/types/Media';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Drawer, DrawerContent, DrawerTrigger } from '@/components/ui/drawer';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { createReusableTemplate, useMediaQuery } from '@vueuse/core';
import { useMedia } from '@/services/Media';
import { HomeIcon, FunnelIcon,UserIcon, Cog6ToothIcon, HomeModernIcon, UserGroupIcon, ChevronDownIcon } from '@heroicons/vue/24/solid';
import { CaretSortIcon } from '@radix-icons/vue';
import Cookies from 'js-cookie';

const { fetchMedia: gqlFetchMedia } = useMedia();

// Define props passed into the component
const props = defineProps<{
	vhost: string | null;
}>();

// Define emitted events
const emit = defineEmits<{
	(event: 'on-select', item: Media): void;
	(event: 'on-query', item: String): void;
	(event: 'on-filter', type: String): void;
}>();

// Reactive state
const loading = ref(false);
const filterShown = ref(false);
const totalMedia = ref(0);
const mediaList = ref<Media[]>([]);
const searchQuery = ref('');

// Function to truncate text if it exceeds a certain length
const truncateText = (text: string, maxLength: number): string => {
	if (!text || text.length <= maxLength) {
		return text;
	}
	return `${text.substring(0, maxLength)}...`;
};

// Emit event when a media item is selected
const selectMedia = (mediaItem: Media) => {
	emit('on-select', mediaItem);
	searchQuery.value='';
	mediaList.value=[];
};
const sendSearchTerm = () => {
	emit('on-query', searchQuery.value);
};
const toggleFilter = () => {
	if(filterShown.value)  {
		emit('on-filter', '');
		filterShown.value=false;
	}
	else {
		emit('on-filter', 'media');
		filterShown.value=true;
	}

};

// Function to highlight search terms in text
const highlightWord = (text: string, searchWord: string, cssClass: string): string => {
	if (!searchWord) return text;

	// Escape special characters for regex
	const escapedSearchWord = searchWord.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
	// Create a case-insensitive regex
	const regex = new RegExp(`(${escapedSearchWord})`, 'gi');

	// Replace matches with a span wrapper
	return text.replace(regex, `<span class="${cssClass}">$1</span>`);
};

// Function to fetch media data from the backend
const fetchMedia = async () => {
	loading.value = true;

	try {
		const fields: (keyof Media)[] = ['id', 'title', 'tags', 'uid', 'status', 'message', 'publicUrl', 'thumbUrl', 'filetype', 'extension'];
		const { mediaData, totalCount } = await gqlFetchMedia(
			Number(props.vhost),
			{ query: searchQuery.value },
			'upload DESC',
			0,
			25,
			fields
		);
		mediaList.value = mediaData;
		totalMedia.value = totalCount;
	} catch (error) {
		console.error('Error fetching media:', error);
	} finally {
		loading.value = false;
	}
};

// Watch for changes in the search query and log updates (for debugging)
watch(searchQuery, (newQuery) => {
	console.log('Search query updated:', newQuery);
});

// Update search query and fetch media based on input event
const updateSearchQuery = (event: InputEvent) => {
	searchQuery.value = (event.target as HTMLInputElement).value;
	fetchMedia();
};

// Lifecycle hook for initialization
onMounted(() => {
	// Initial fetch or setup actions can go here if needed
});

// Reusable template for project list (placeholders for your specific templates)
const [UseTemplate, ProjectList] = createReusableTemplate();

</script>

<template>
	<div class="relative z-40 flex justify-start items-center px-2 lg:px-8 gap-2">
		<div class="flex-1 min-w-0 px-1 lg:px-3">
			<Command class="border border-muted-foreground shadow-md justify-start items-left text-secondary-foreground text-sm font-medium h-9 w-full bg-background">
				<CommandInput v-bind:value="searchQuery" placeholder="Find Media" class="flex border-b-0 items-center h-8 text-secondary-foreground" @input="updateSearchQuery" @keyup.enter="sendSearchTerm"/>
				<CommandList v-if="searchQuery!=''" class="absolute left-[50px] top-[46px] w-[600px] border">
				<CommandEmpty>No results found.</CommandEmpty>
				<CommandGroup>
				<CommandItem v-for="media in mediaList" :key="media.id" :value="media.id"  class="bg-background px-0 py-0 rounded-0 max-h-64">
				<div class="flex justify-start  hover:bg-secondary border border-transparent w-full hover:border border-background bg-background" @click.prevent="selectMedia(media)">
					<div class="basis-1/5 p-2">
						<media-thumb :mediaItem="media"/>
					</div>
					<div class="p-2 basis-4/5 ">
						<h2 class="text-base text-secondary-foreground ">{{ media.title }}</h2>
						<p class="pl-3 text-xs text-secondary-foreground " v-if="media.extension!='vtt'" >{{ highlightWord(truncateText(media.message,200),searchQuery,"text-blue-600") }}</p>
						<p class="pl-3 text-xs text-secondary-foreground "  v-else>Video Caption File</p>
					</div>
				</div>
				</CommandItem>
				</CommandGroup>
				</CommandList>
			</Command>
		</div>
		<div class="flex-shrink-0">
			<Button @click.prevent="toggleFilter" variant="secondary"><FunnelIcon class="h-5 w-5"/><span class="hidden lg:inline">Filters</span> <ChevronDownIcon class="h-5 w-5"/></Button>
		</div>
	</div>
</template>
<style scoped>

.result-item {
	display: flex;
	align-items: flex-start;
	gap: 1rem;
	margin-bottom: 1rem;
	cursor: pointer;
}

.result-image {
	width: 50px;
	height: 50px;
	object-fit: cover;
	border-radius: 0.25rem;
}

.result-details {
	flex: 1;
}

.result-range {
	font-size: 0.9rem;
	color: #555;
}

.result-description {
	margin: 0;
	color: #fff;
}

.result-title {
	font-size: 1rem;
	margin: 0;
	color: #69d;
}

</style>

